import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'flexwise_login_attempts';
const ATTEMPT_THRESHOLD = 3;
const EXPIRY_MINUTES = 15;

/**
 * Hook to track failed login attempts for CAPTCHA triggering
 * @returns {Object} { attempts, incrementAttempts, resetAttempts, shouldShowCaptcha }
 */
export function useLoginAttempts() {
    const [attempts, setAttempts] = useState(0);

    // Load attempts from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const { count, timestamp } = JSON.parse(stored);
                const now = Date.now();
                const expiryMs = EXPIRY_MINUTES * 60 * 1000;

                // Check if attempts have expired
                if (now - timestamp < expiryMs) {
                    setAttempts(count);
                } else {
                    // Clear expired attempts
                    localStorage.removeItem(STORAGE_KEY);
                }
            }
        } catch (e) {
            console.warn('Failed to load login attempts from storage:', e);
        }
    }, []);

    const incrementAttempts = useCallback(() => {
        setAttempts((prev) => {
            const newCount = prev + 1;
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({
                    count: newCount,
                    timestamp: Date.now()
                }));
            } catch (e) {
                console.warn('Failed to save login attempts to storage:', e);
            }
            return newCount;
        });
    }, []);

    const resetAttempts = useCallback(() => {
        setAttempts(0);
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            console.warn('Failed to clear login attempts from storage:', e);
        }
    }, []);

    // Skip captcha in development or if hCaptcha site key is not configured
    const captchaSiteKey = import.meta.env.VITE_HCAPTCHA_SITE_KEY;
    const isDev = import.meta.env.DEV;
    const shouldShowCaptcha = !isDev && !!captchaSiteKey && attempts >= ATTEMPT_THRESHOLD;

    return {
        attempts,
        incrementAttempts,
        resetAttempts,
        shouldShowCaptcha,
        threshold: ATTEMPT_THRESHOLD
    };
}
